// src/components/events/EventCard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const CATEGORY_EMOJI = {
  music: '🎵', sport_gaa: '🏐', sport: '🏃', kids: '🧒',
  health: '🏥', farming: '🌾', food: '🍽', education: '📚', social: '🎉',
  community: '🤝', markets: '🛒', arts: '🎭',
  default: '📅'
};

const CATEGORY_BG = {
  music: '#EBF2EB', sport_gaa: '#EBF2EB', sport: '#EBF2EB', kids: '#EBF0F8',
  health: '#F8EBEB', farming: '#F0F4E8', food: '#FDF3E0', education: '#EBF0F8', social: '#FDF3E0',
  markets: '#FDF3E0', arts: '#F0EBFA', community: '#EBF2EB',
  default: '#EBF2EB'
};

const REPEAT_LABEL = {
  weekly: '🔁 Weekly', fortnightly: '🔁 Fortnightly',
  monthly: '🔁 Monthly', custom: '🔁 Recurring'
};

const formatEventDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return format(d, 'EEE d MMM · h:mmaaa').replace('am', 'am').replace('pm', 'pm');
};

// ── HERO (big featured card) ─────────────────────────────
export const EventCardHero = ({ event }) => {
  const navigate = useNavigate();
  const emoji = CATEGORY_EMOJI[event.category] || CATEGORY_EMOJI.default;

  return (
    <div style={heroStyles.card} onClick={() => navigate(`/event/${event.id}`)}>
      {/* Image / emoji hero */}
      <div style={heroStyles.hero}>
        {event.imageUrl
          ? <img src={event.imageUrl} alt={event.title} style={heroStyles.img} />
          : <span style={{ fontSize: 60 }}>{emoji}</span>
        }
        {event.repeat && event.repeat !== 'once' && (
          <div style={heroStyles.recurringBadge}>{REPEAT_LABEL[event.repeat]}</div>
        )}
      </div>
      {/* Body */}
      <div style={heroStyles.body}>
        <div style={heroStyles.chips}>
          <span className="chip chip-date">{formatEventDate(event.nextDate || event.date)}</span>
          <span className="chip chip-sage" style={{ textTransform: 'capitalize' }}>
            {event.category}
          </span>
          {event.eventType === 'free' || !event.eventType
            ? <span className="chip chip-sage">Free entry</span>
            : event.eventType === 'paid'
            ? <span className="chip chip-warm">🎟 {event.eventCharge}</span>
            : null
          }
        </div>
        <div style={heroStyles.title}>{event.title}</div>
        <div style={heroStyles.meta}>
          <span>📍 {event.venue}</span>
          {event.interestCount > 0 && (
            <span style={heroStyles.interestPill}>❤️ {event.interestCount} interested</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── SMALL (list card) ────────────────────────────────────
export const EventCardSmall = ({ event }) => {
  const navigate = useNavigate();
  const emoji = CATEGORY_EMOJI[event.category] || CATEGORY_EMOJI.default;
  const bg = CATEGORY_BG[event.category] || CATEGORY_BG.default;

  return (
    <div style={smallStyles.card} onClick={() => navigate(`/event/${event.id}`)}>
      <div style={{ ...smallStyles.img, background: bg }}>
        {event.imageUrl
          ? <img src={event.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
          : <span style={{ fontSize: 26 }}>{emoji}</span>
        }
      </div>
      <div style={smallStyles.body}>
        <div style={smallStyles.title}>{event.title}</div>
        <div style={smallStyles.meta}>{formatEventDate(event.nextDate || event.date)} · {event.venue}</div>
        <div style={smallStyles.chips}>
          {event.eventType === 'paid'
            ? <span className="chip chip-warm-pale" style={{ fontSize: 10 }}>🎟 {event.eventCharge}</span>
            : <span className="chip chip-sage" style={{ fontSize: 10 }}>Free</span>
          }
          {event.repeat && event.repeat !== 'once' && (
            <span className="chip chip-muted" style={{ fontSize: 10 }}>{REPEAT_LABEL[event.repeat]}</span>
          )}
        </div>
      </div>
      <span style={smallStyles.arrow}>›</span>
    </div>
  );
};

const heroStyles = {
  card: {
    background: 'var(--sage)', border: '1px solid #C8D9C9',
    borderRadius: 22, overflow: 'hidden',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 0.15s',
  },
  hero: {
    height: 150,
    background: 'linear-gradient(140deg, var(--sage-light), var(--sage))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    position: 'relative',
  },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  recurringBadge: {
    position: 'absolute', top: 10, right: 12,
    background: 'rgba(255,255,255,0.7)',
    borderRadius: 100, padding: '4px 10px',
    fontSize: 11, fontWeight: 700, color: 'var(--sage-dark)',
  },
  body: { padding: '14px 16px 16px' },
  chips: { display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap' },
  title: {
    fontSize: 20, fontWeight: 800, color: 'var(--sage-dark)',
    lineHeight: 1.2, marginBottom: 8, letterSpacing: '-0.2px',
  },
  meta: {
    fontSize: 12, color: 'var(--sage-deep)', fontWeight: 600,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  interestPill: {
    background: 'rgba(255,255,255,0.5)',
    borderRadius: 100, padding: '3px 10px',
    fontSize: 11, color: 'var(--sage-dark)', fontWeight: 700,
  }
};

const smallStyles = {
  card: {
    background: 'var(--white)', border: '1.5px solid #C0D4C0',
    borderRadius: 16,
    display: 'flex', gap: 12, padding: 13,
    cursor: 'pointer',
    boxShadow: 'var(--shadow-sm)',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 0.15s',
  },
  img: {
    width: 60, height: 60, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, overflow: 'hidden',
  },
  body: { flex: 1, minWidth: 0 },
  title: {
    fontSize: 15, fontWeight: 700, color: 'var(--ink)',
    marginBottom: 3, letterSpacing: '-0.1px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  meta: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginBottom: 5 },
  chips: { display: 'flex', gap: 5 },
  arrow: { color: 'var(--border)', fontSize: 20, alignSelf: 'center', fontWeight: 300 },
};
